import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import IOfferInfoRow from 'api/interfaces/offers/IOfferInfoRow';
import LinkButton from 'components/layout/buttons/LinkButton';
import translations from 'services/translations/translations';

import 'style/offers/OfferInfoRowsEditor.css';

const MAX_TITLE_LENGTH = 100;
const MAX_TEXT_LENGTH = 1000;

/**
 * The own rows of the information table at the end of the offer PDF, printed after the
 * collaterals: a title at the left (in capitals on the PDF) and a free text at the right.
 * Used in the offer defaults of a department and in the basic information of an offer.
 */
const OfferInfoRowsEditor = ({
  disabled,
  onChange,
  rows,
}: {
  disabled?: boolean;
  onChange: (rows: IOfferInfoRow[]) => void;
  rows: IOfferInfoRow[];
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const tr = (key: string) => t(`${translations.offers}:infoRows.${key}`);

  const change = (index: number, field: keyof IOfferInfoRow, value: string) =>
    onChange(
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );

  return (
    <div className="offer-info-rows">
      {rows.map((row, index) => (
        // The rows have no ids; they are edited in place
        // eslint-disable-next-line react/no-array-index-key
        <div className="offer-info-row" key={index}>
          <Input
            aria-label={tr('Title')}
            className="offer-info-row-title"
            disabled={disabled}
            maxLength={MAX_TITLE_LENGTH}
            onChange={(event) => change(index, 'title', event.target.value)}
            placeholder={tr('Title')}
            value={row.title}
          />
          <Input
            aria-label={tr('Text')}
            className="offer-info-row-text"
            disabled={disabled}
            maxLength={MAX_TEXT_LENGTH}
            onChange={(event) => change(index, 'text', event.target.value)}
            placeholder={tr('Text')}
            rows={1}
            type="textarea"
            value={row.text}
          />
          {disabled ? null : (
            <button
              aria-label={tr('Remove')}
              className="offer-info-row-remove"
              onClick={() =>
                onChange(rows.filter((_, rowIndex) => rowIndex !== index))
              }
              title={tr('Remove')}
              type="button"
            >
              ✕
            </button>
          )}
        </div>
      ))}
      {disabled ? null : (
        <LinkButton
          onClick={() => onChange([...rows, { text: '', title: '' }])}
        >
          {`+ ${tr('Add row')}`}
        </LinkButton>
      )}
    </div>
  );
};

export default OfferInfoRowsEditor;
